import { Button, Dropdown, message, theme, Tooltip } from "antd";
import {
  AppstoreOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  LoadingOutlined,
  StopOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import Icon from "@ant-design/icons";
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { Flex } from "src/components/Flex";
import { SearchState } from "src/search/types";
import { QuestionInput } from "src/search/components/inputs/QuestionInput";
import {
  ensureState,
  serializeParams,
} from "src/search/lib/params-state-reducer";
import { useApplicationContext } from "src/hooks/useApplication";
import { DashboardEditor } from "./DashboardEditor";
import {
  DashboardRuntime,
  DashboardsPageContext,
  isDashboardId,
  isNewDashboardId,
  isStartScreenId,
} from "./context";

import { v4 as uuidv4 } from "uuid";
import { Input, Select } from "antd";
import { ComponentProps, Dispatch, SetStateAction } from "react";
import {
  CompassOutlined,
  FileMarkdownOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Duration } from "src/utils/date";
import {
  DashboardV1State,
  DeepLinkType,
  SnippetV1State,
} from "pages/api/deep-links/types";
import { VizIcon } from "src/components/VizIcon";
import { unserializeParams } from "src/search/lib/params-state-reducer";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { SaveIcon } from "src/components/SaveIcon";
import { duplicateLink } from "pages/api-clients/deep-links";
import { SpaceItemType } from "pages/api/[orgId]/spaces/types";
import { StarIcon } from "src/components/StarIcon";
import { ContentViews } from "../explore/lib/useContentView";
import { AddToSpacePicker } from "../space/AddToSpacePicker";
import { FavoriteButton } from "src/components/FavoriteButton";
import { BoardItem } from "src/components/useLibrary";

export function PrimaryQuery(
  props: PropsWithChildren<DashboardsPageContext & DashboardRuntime>
) {
  const orgId = useApplicationContext().org_id!;
  const searchState = useMemo(
    () => ensureState(props.searchState, orgId),
    [props.searchState, orgId]
  );
  const [inputPing, setInputPing] = useState(0);
  const hardcore = serializeParams(props.searchState);
  const seed = `${props.dashboardId}-${props.readyToEdit}-${inputPing}-${hardcore}`;
  const propsRef = useRef(props);
  propsRef.current = props;
  const readyToEdit = props.readyToEdit;
  useEffect(() => {
    if (readyToEdit) {
      const preloadedSearchState = sessionStorage.getItem(
        "preloadedSearchState"
      );
      sessionStorage.removeItem("preloadedSearchState");
      if (preloadedSearchState) {
        try {
          const { duration, endDate, startDate, filters } =
            JSON.parse(preloadedSearchState);
          // To quote Chicago,
          //   "Look away. Baaaaaby look away."
          //
          // Update: this doIt hack may be unnecessary with the hardcore seed
          const doIt = () => {
            propsRef.current.setSearchState({
              duration,
              endDate,
              startDate,
              filters,
            });
            setInputPing((ping) => ping + 1);
          };
          doIt();
          setTimeout(doIt, 10);
          setTimeout(doIt, 1000);
          // you were supposed to look away.
        } catch {}
      }
    }
  }, [readyToEdit]);
  return (
    <Flex direction="column" style={{ height: "100%", width: "100%" }}>
      <div
        style={{
          gap: 16,
          display: "flex",
          height: 72,
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          minHeight: 0,
          padding: "0 16px",
        }}
      >
        <div
          style={{
            flex: props.isLocked ? undefined : 1,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <DashboardController {...props} />
        </div>
        <div style={{ maxWidth: "100%" }}>
          {props.readyToEdit && props.isLocked && (
            <QuestionInput
              collapsed
              seed={seed}
              value={searchState}
              onChange={props.setSearchState}
              afterReset={() => {
                // TODO pretty heavy. cards don't reset, so we refresh. For now.
                window.location.reload(); // 😲
              }}
              resetTitle="Reset to defaults"
              resetIcon={<UndoOutlined />}
              hideDisabled
              options={{
                disableGroupBys: true,
                disableMeasures: true,
                disableSelect: true,
                disableSearchTerm: true,
                disableCompareWith: true,
                disableFormula: true,
                disableHaving: true,
                disableSuggestions: true,
                addFromTeamSnippet: false,
              }}
              snippetTransformer={(state) =>
                ({
                  duration: state.duration,
                  startDate: state.startDate,
                  endDate: state.endDate,
                  filters: state.filters,
                } as SearchState)
              }
              snippetOptionFilter={(snippet) => {
                const json = snippet?.json as SnippetV1State | undefined;
                return (json?.params?.filters?.length || 0) > 0;
              }}
            />
          )}
        </div>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
          width: "100%",
        }}
      >
        {props.children}
      </div>
    </Flex>
  );
}

function DashboardController(props: DashboardsPageContext & DashboardRuntime) {
  return (
    <div style={{ maxWidth: "100%", minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          flex: 1,
          alignItems: "center",
          gap: 8,
          padding: 2,
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        {props.isLocked ? (
          <div
            style={{ fontSize: 24, fontWeight: 600, minWidth: 0 }}
            className="trunc"
          >
            {props.name || props.dashboardId}
          </div>
        ) : (
          <Input
            style={{ height: 44, borderRadius: 14 }}
            placeholder="Dashboard Name"
            onChange={(e) => props.setName(e.target.value)}
            value={props.name}
          />
        )}
        <ButtonBar {...props} />
      </div>
      {props.description && (
        <div className="line-clamp-1">{props.description}</div>
      )}
    </div>
  );
}

function ButtonBar(props: ComponentProps<typeof DashboardController>) {
  const { token } = theme.useToken();
  const app = useApplicationContext();
  const deep = useDeepLinks(DeepLinkType.BOARD);
  const board = deep.links.data?.find((link) => link.id === props.dashboardId);
  const isCreatingNew = isNewDashboardId(props.dashboardId);
  const [isEditingDetails, editDetails] = useState(false);
  const [showingSpacePicker, showAddToSpacePicker] = useState(false);

  const boardIsGlobal = !board?.orgId;
  const boardIsShared = !board?.userId;
  const boardIsPrivate = !boardIsGlobal && !boardIsShared;

  const userIsGlobal = app.role == "super" || app.user?.superuser;
  const userIsShared = app.role == "admin";

  const canEdit =
    (boardIsGlobal && userIsGlobal) ||
    (boardIsShared && (userIsShared || userIsGlobal)) ||
    boardIsPrivate;

  const editorTags = (
    deep.links.data
      ?.map((board_) =>
        boardIsGlobal && !board_.orgId
          ? (board_.json as DashboardV1State | undefined)?.tags
          : boardIsShared && !board_.userId
          ? (board_.json as DashboardV1State | undefined)?.tags
          : boardIsPrivate && board_.userId
          ? (board_.json as DashboardV1State | undefined)?.tags
          : undefined
      )
      .filter(Boolean) || []
  ).flat() as string[];

  return (
    <>
      {isEditingDetails && canEdit && (
        <DashboardEditor
          {...props}
          tags={editorTags}
          onClose={() => editDetails(false)}
        />
      )}
      {showingSpacePicker && board && (
        <AddToSpacePicker
          open
          target={
            document.querySelector("#primary-query-menu-button") as HTMLElement
          }
          onClose={() => showAddToSpacePicker(false)}
          item={{
            type: SpaceItemType.Board,
            name: board.name,
            config: { deep_link_id: board.id },
          }}
        >
          {null}
        </AddToSpacePicker>
      )}
      <Flex gap={4}>
        <FavoriteButton item={board as BoardItem} />
        {props.isLocked ? (
          <Dropdown
            trigger={["click"]}
            placement={"bottom"}
            arrow
            menu={{
              items: [
                {
                  key: "rearrange",
                  icon: <AppstoreOutlined />,
                  label: <span>Edit cards</span>,
                  disabled:
                    !canEdit || props.mutationInProcess || !props.userCanEdit,
                  onClick() {
                    props.unlock();
                  },
                },
                {
                  key: "configure",
                  icon: <EditOutlined />,
                  label: <span>Save settings</span>,
                  disabled:
                    !canEdit || props.mutationInProcess || !props.userCanEdit,
                  onClick() {
                    editDetails(true);
                  },
                },
                {
                  key: "space",
                  icon: <Icon component={() => <PlusOutlined />} />,
                  label: <span>Add board to space</span>,
                  onClick() {
                    showAddToSpacePicker(true);
                  },
                },
                {
                  key: "duplicate",
                  icon: <CopyOutlined />,
                  label: <span>Duplicate</span>,
                  disabled:
                    !isDashboardId(props.dashboardId) ||
                    props.mutationInProcess,
                  onClick() {
                    if (!isDashboardId(props.dashboardId)) return;
                    duplicateLink(
                      props.dashboardId!,
                      app.org_id!,
                      app.user!.id
                    ).then((link) => {
                      if (link) {
                        props.openDashboard(link.id);
                        setTimeout(() => {
                          // we could also invalidate the query cache instead..
                          window.location.reload();
                        }, 10);
                      }
                    });
                  },
                },
                {
                  key: "delete",
                  icon: <DeleteOutlined />,
                  label: <span>Delete</span>,
                  danger: true,
                  disabled:
                    !canEdit ||
                    props.mutationInProcess ||
                    !props.userCanEdit ||
                    isCreatingNew,
                  onClick() {
                    props.onDelete();
                    props.lock();
                  },
                },
              ],
            }}
          >
            <Button
              id="primary-query-menu-button"
              style={{
                paddingLeft: 8,
                paddingRight: 8,
                justifyContent: "center",
                alignItems: "center",
              }}
              type="text"
              shape="round"
              disabled={props.mutationInProcess || !props.userCanEdit}
            >
              {props.mutationInProcess ? (
                <LoadingOutlined />
              ) : isStartScreenId(props.dashboardId) ? null : (
                <EllipsisOutlined style={{ color: token.colorTextTertiary }} />
              )}
            </Button>
          </Dropdown>
        ) : (
          <>
            <QuestionsLoader
              data={props.data}
              layout={props.layout}
              setData={props.setData}
              setLayout={props.setLayout}
            />
            <Tooltip title="Discard Changes">
              <Button
                onClick={() => {
                  props.onCancel();
                  props.lock();
                }}
                type="text"
                shape="round"
                style={{
                  width: 32,
                  padding: 0,
                  display: props.isLocked ? "none" : "initial",
                }}
              >
                <StopOutlined />
              </Button>
            </Tooltip>
            <Tooltip
              title={
                props.userCanCreate
                  ? "Save Changes"
                  : "You have read-only access"
              }
            >
              <Button
                disabled={!props.userCanCreate}
                key="save"
                onClick={() => {
                  props.onSave();
                  props.lock();
                }}
                shape="round"
                type="primary"
                style={{
                  paddingLeft: 8,
                  paddingRight: 8,
                  display: props.isLocked ? "none" : "initial",
                }}
              >
                <SaveIcon />
              </Button>
            </Tooltip>
          </>
        )}
      </Flex>
    </>
  );
}

function QuestionsLoader(props: {
  data: DashboardV1State["data"];
  layout: DashboardV1State["layout"];
  setData: Dispatch<SetStateAction<DashboardV1State["data"]>>;
  setLayout: Dispatch<SetStateAction<DashboardV1State["layout"]>>;
}) {
  const orgId = useApplicationContext().org_id!;
  const { token } = theme.useToken();
  const { links } = useDeepLinks(DeepLinkType.EXPLORE);
  const questions = useMemo(
    () => [
      {
        key: "markdown-card",
        name: "-- markdown html text card",
        label: (
          <Flex start centeredY gap={8}>
            <div style={{ paddingTop: 2 }}>
              <FileMarkdownOutlined />
            </div>
            <span>Add Markdown Card</span>
          </Flex>
        ),
        value: "markdown",
        disabled: false,
        state: undefined,
      },
      {
        key: "question-card",
        name: "-- explore question new empty card",
        label: (
          <Flex start centeredY gap={8}>
            <div style={{ paddingTop: 2 }}>
              <CompassOutlined />
            </div>
            <span>Add Question Card</span>
          </Flex>
        ),
        value: "question",
        state: undefined,
      },
      {
        label: "Saved Questions",
        options: (links.data || [])
          .map((link) => {
            let vizValue = ContentViews.default;
            let state = {} as {
              params: SearchState;
              viz: { value: ContentViews; config: any };
            };
            try {
              const data = JSON.parse(link.state);
              const params = unserializeParams(data.serialized);
              const viz = (data.viz || []).at(0);
              if (viz?.value) vizValue = viz.value;
              state = { params, viz };
            } catch {}
            const disabled =
              props.layout.findIndex((layout_) => layout_.i === link.id) >= 0;
            return {
              key: "key" + link.id,
              name: link.name.toLowerCase(),
              label: (
                <Flex start centeredY gap={8}>
                  <div style={{ opacity: disabled ? 0.2 : 1, paddingTop: 2 }}>
                    <VizIcon inactive={disabled} value={vizValue} size={15} />
                  </div>
                  <span
                    style={{
                      display: "inline-block",
                      maxWidth: 250,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {link.name}
                  </span>
                </Flex>
              ),
              value: link.id,
              disabled,
              state,
            };
          })
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || "")),
      },
    ],
    [links.data, props.layout]
  );
  return (
    <div
      style={{
        display: "block",
        width: 120,
        borderRadius: 14,
        height: 46,
        paddingTop: 5,
        backgroundColor: token.colorBgContainer,
        border: `solid 1px ${token.colorBorder}`,
      }}
    >
      <Select
        mode="multiple"
        showSearch
        bordered={false}
        placeholder={
          <span
            style={{
              fontWeight: 400,
              fontSize: 13,
              color: token.colorText,
            }}
          >
            <PlusOutlined style={{ cursor: "pointer" }} /> Add Card
          </span>
        }
        filterOption={(input, option) =>
          (option?.name || "").includes(input.toLowerCase())
        }
        filterSort={(a, b) => {
          if (a.key === "markdown-card") return -1;
          if (b.key === "markdown-card") return 1;
          if (a.key === "question-card") return -1;
          if (b.key === "question-card") return 1;
          return (a?.name || "").localeCompare(b?.name || "");
        }}
        placement="bottomRight"
        popupMatchSelectWidth={false}
        style={{
          display: "block",
        }}
        options={questions}
        loading={links.isLoading}
        value={[]}
        onChange={(_, options) => {
          if (!Array.isArray(options)) return; // unexpected.
          const option = options.at(0);
          if (!option) return; // still unexpected.

          const i = uuidv4();
          props.setLayout((state) => [
            ...state,
            {
              i: i,
              x: ((options.length - 1) * 2) % 12, // TODO fix this, should depend on dashboard column settings
              y: Number.POSITIVE_INFINITY, // puts it at the bottom
              w: 6,
              h: 3,
            },
          ]);
          props.setData((state) => ({
            ...state,
            [i]:
              option.state && option.name
                ? {
                    type: DeepLinkType.EXPLORE,
                    style: {},
                    title: option.name,
                    showTitle: true,
                    centerTitle: false,
                    transparent: false,
                    state: option.state,
                  }
                : option.key === "markdown-card"
                ? {
                    type: "markdown",
                    style: {},
                    transparent: false,
                    markdown: `# Markdown block\n---\nEdit this card to add your own content`,
                  }
                : {
                    type: DeepLinkType.EXPLORE,
                    style: {},
                    title: "New Question Card",
                    showTitle: true,
                    centerTitle: false,
                    transparent: false,
                    state: {
                      params: {
                        orgId,
                        duration: "1PD" as Duration,
                        select: ["Event"],
                      },
                      viz: { value: ContentViews.default, config: {} },
                    },
                  },
          }));
          const scroller = document.querySelector("#dashboard-scroll-parent");
          if (scroller) scroller.scrollTo(0, scroller.scrollHeight);
        }}
      />
    </div>
  );
}
